import { ResourceArticle } from '../../types';

export const ARTICLES_PART_2: ResourceArticle[] = [
  {
    id: 'guide-11-auspost-scams',
    slug: 'australia-post-parcel-delivery-scams',
    title: 'Australia Post Delivery Surcharge & Redelivery Scams: How Fake AusPost Texts Work',
    category: 'Scams',
    date: '28 July 2026',
    author: {
      name: 'Sarah Chen',
      role: 'Consumer Rights Specialist, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Australia Post never asks for credit card payments or customs fees via unsolicited SMS links.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Verify genuine parcel tracking exclusively inside the official Australia Post app.',
    summary: 'Fake parcel delivery SMS notices are the highest volume phishing threat in Australia. Learn how cybercriminals harvest payment cards and how to verify real delivery status.',
    keyTakeaways: [
      'Australia Post will never send you an SMS with a link asking for extra delivery fees, handling charges, or updated address details.',
      'Scammers use fake parcel codes (e.g. `AU-88219-X`) and non-official domains like `auspost-redelivery-au.net`.',
      'The requested fee is intentionally tiny (e.g. $1.95 or $2.40) to trick victims into entering 16-digit payment card numbers.',
      'Always track parcels directly inside the official AusPost app or at `auspost.com.au` using the merchant tracking number.'
    ],
    content: [
      'With millions of Australians ordering parcels weekly from Amazon, eBay, Kmart, and local retailers, delivery phishing messages have become a permanent fixture in Australian inboxes.',
      'The message claims: "AusPost: Your package could not be delivered due to an incomplete delivery address and an unpaid duty handling fee ($2.10). Please update your details within 24 hours to prevent parcel return: [Link]".',
      'In this article, we explain how this credential harvesting attack unfolds and how to ensure you never fall victim.'
    ],
    sections: [
      {
        heading: 'The Mechanics of the Delivery Phishing Funnel',
        body: [
          'The attack follows three deceitful stages:',
          '1. The Micro-Fee Trap: By asking for a nominal fee of $1.90 to $3.50, victims believe the cost is too low to be a scam. However, the scammer is not interested in the $2 fee—they want your 16-digit card number, CVV, and full identity credentials.',
          '2. The Cloned Website: The malicious link opens a pixel-perfect replica of the Australia Post website featuring red branding and official logos.',
          '3. Instant Card Compromise: Once submitted, the credit card details are immediately sold on underground forums or used for high-value purchases before the victim notices.'
        ]
      }
    ],
    actionSteps: [
      'Never click links in delivery SMS messages.',
      'Track parcels using the official AusPost app.',
      'Forward suspicious text messages to 7726 (SPAM).',
      'Use single-use virtual cards or digital wallets for online shopping.'
    ],
    relatedSlugs: ['linkt-transurban-toll-sms-scam-breakdown', 'preventing-payid-marketplace-scams', 'online-shopping-scams-fake-storefronts-australia']
  },
  {
    id: 'guide-12-ai-voice-cloning',
    slug: 'ai-voice-cloning-family-emergency-scams',
    title: 'AI Voice Cloning & "Hi Mum" Family Emergency Scams: How Cybercriminals Fake Your Relatives\' Voices',
    category: 'Scams',
    date: '26 July 2026',
    author: {
      name: 'Marcus Vance',
      role: 'Fraud Intelligence Analyst, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 8,
    imageUrl: 'https://images.unsplash.com/photo-1589254065878-42c9da997008?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'AI voice cloning models can recreate an Australian family member’s voice from just 3 seconds of social media audio.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Establishing an unwritten family security passphrase prevents voice impersonation fraud.',
    summary: 'Generative AI voice cloning technology allows fraudsters to synthesize your children or parents\' voices over the phone. Learn how to spot synthetic audio and establish family emergency passphrases.',
    keyTakeaways: [
      'Modern AI voice models require only 3 to 10 seconds of clear audio (scraped from TikTok, Instagram, or voicemail) to clone a person’s voice.',
      'Scammers combine cloned voice notes with extreme emotional distress (claiming a car accident, arrest, or broken phone) to provoke instant bank transfers.',
      'Establish a secret, offline "Family Emergency Safe Word" that must be spoken before transferring funds in any emergency scenario.',
      'Always hang up and call your family member back on their known, original phone number.'
    ],
    content: [
      'The evolution of artificial intelligence has given rise to one of the most frightening fraud vectors in Australian history: real-time voice cloning and synthetic deepfakes.',
      'What began as the text-based "Hi Mum" WhatsApp scam has evolved into convincing telephone calls where a voice that sounds identical to your daughter, son, or grandchild cries out in panic, claiming they are stranded, arrested, or in hospital and desperately need an urgent bank transfer.',
      'In this article, we explain how voice synthesis technology operates and provide the definitive protocol to protect your household.'
    ],
    sections: [
      {
        heading: 'How Voice Scrapers Harvest Audio Samples',
        body: [
          'Cybercriminals scrape public social media profiles (Instagram Reels, TikTok videos, YouTube podcasts, and public voicemail greetings) to acquire high-quality audio clips of family members.',
          'Using neural voice synthesis engines, the scammer uploads the sample and types a script. The software outputs audio matching the victim’s exact pitch, accent, intonation, and vocal cadence.',
          'When combined with spoofed caller ID numbers, the incoming call appears entirely authentic to a panicked parent or grandparent.'
        ]
      },
      {
        heading: 'The 3-Rule Family Defense Protocol',
        body: [
          '1. The Family Safe Word: Agree upon a unique, unwritten family passphrase (e.g. `blue-kangaroo-bondi`) known only to immediate family members. If anyone calls claiming an emergency, they must provide the passphrase.',
          '2. The Hang-Up-and-Call-Back Rule: Never transfer money during the initial call. Hang up and dial your family member directly on their known contact number.',
          '3. Ask a Fact-Check Question: Ask a personal question that no AI could find online (e.g. "What did we have for dinner last Sunday?" or "What was the name of our childhood pet?").'
        ]
      }
    ],
    actionSteps: [
      'Establish a secret family emergency safe word with your children and parents today.',
      'Set social media accounts (Instagram, TikTok) to private to prevent audio scraping.',
      'Never send emergency funds based solely on an unexpected phone call or text.',
      'Enroll elderly relatives in the IDFraud.au Family Protection Shield.'
    ],
    relatedSlugs: ['protecting-elderly-parents-from-digital-scams', 'sim-swapping-mobile-porting-fraud-australia', 'preventing-payid-marketplace-scams']
  },
  {
    id: 'guide-13-bank-impersonation',
    slug: 'bank-impersonation-fraud-australian-banking',
    title: 'Australian Bank Impersonation Scams: Why Your Bank Never Asks for NetCode over the Phone',
    category: 'Scams',
    date: '24 July 2026',
    author: {
      name: 'Lachlan Hayes',
      role: 'Identity Security Lead, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 8,
    imageUrl: 'https://images.unsplash.com/photo-1501167786227-4cba60f6d58f?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Australian bank fraud specialists will never call you demanding one-time SMS NetCodes or fund transfers to "safe accounts".',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1559526324-4b87b5e36e44?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Verify suspected banking fraud calls by dialing the phone number on the back of your physical card.',
    summary: 'Bank impersonation scams trick Australians into transferring savings to "safe vault accounts" or disclosing SMS NetCodes. Discover how to identify rogue calls and protect your balances.',
    keyTakeaways: [
      'Australian banks (CommBank, NAB, ANZ, Westpac, Macquarie) will NEVER ask you to transfer money to a "safe holding account" to protect it.',
      'Bank staff will NEVER ask you to read out or enter a one-time SMS NetCode or password over the phone.',
      'Scammers use Caller ID spoofing to display your bank’s real 13-digit phone number on your caller display.',
      'If you receive a suspicious call, hang up immediately and dial the official phone number printed on the back of your physical bank card.'
    ],
    content: [
      'Bank impersonation fraud is responsible for the single largest dollar value of scam losses in Australia. Scammers masquerade as fraud investigators from major Australian banks, using sophisticated social engineering to panic customers into authorizing catastrophic transfers.',
      'The caller informs you that "unauthorized transactions have been detected in Queensland or overseas" and claims that your account is being compromised by internal bank staff.',
      'In this guide, we expose the exact deception script used by bank impostors and clarify the official operating procedures of legitimate Australian financial institutions.'
    ],
    sections: [
      {
        heading: 'The Myth of the "Safe Holding Account"',
        body: [
          'The core deceit of bank impersonation fraud is convincing the customer that their funds must be temporarily transferred to a "reserve safety account" or "encrypted treasury vault" while the fraud investigation is underway.',
          'In reality, banks have the technical authority to freeze and protect accounts instantly without moving a single dollar. Any request to transfer funds to another BSB and Account Number—regardless of what the caller claims—is transferring your money straight into a criminal mule account.'
        ]
      }
    ],
    actionSteps: [
      'Hang up on any caller asking for SMS NetCodes, passwords, or fund transfers.',
      'Call the official phone number printed on the back of your bank card.',
      'Enable push notifications for all card transactions in your mobile banking app.',
      'Report bank impersonation attempts to Scamwatch and AFCA.'
    ],
    relatedSlugs: ['preventing-payid-marketplace-scams', 'sim-swapping-mobile-porting-fraud-australia', 'remote-access-scams-teamviewer-anydesk']
  },
  {
    id: 'guide-14-rental-applications',
    slug: 'rental-application-id-theft-watermarking',
    title: 'Protecting Identity Documents During Rental Applications: Safe ID Watermarking for Australian Tenants',
    category: 'Australian ID Protection',
    date: '22 July 2026',
    author: {
      name: 'Sarah Chen',
      role: 'Consumer Rights Specialist, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Always watermark copies of your licence and passport before uploading them to tenancy platforms.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Digital watermarks prevent real estate data breaches from exposing raw unredacted credentials.',
    summary: 'Australian tenancy applications demand exhaustive 100-point identity documents. Learn how real estate data breaches happen and how to watermark ID copies to prevent secondary fraud.',
    keyTakeaways: [
      'Tenancy applications routinely collect driver licences, passports, Medicare cards, bank statements, and payslips.',
      'Real estate agencies and third-party tenancy platforms have been frequent targets of data breaches due to insecure storage practices.',
      'Applying a digital watermark stating "COPY — FOR TENANCY APPLICATION AT [AGENCY] ONLY" neutralizes the resale value of stolen scans.',
      'You are not legally required to provide un-redacted bank account numbers or Tax File Numbers on tenancy applications.'
    ],
    content: [
      'In Australia’s competitive rental market, prospective tenants are required to submit extensive personal dossiers to property managers via digital tenancy platforms and email.',
      'These application packages contain everything required to steal a human identity: clear color scans of driver licences, passports, Medicare cards, three months of detailed bank statements, employment contracts, and vehicle registrations.',
      'When property management agencies suffer breaches or use unsecured cloud buckets, thousands of tenant identity dossiers are leaked onto illicit forums. In this guide, we show you how to protect your documents before submitting any rental application.'
    ],
    sections: [
      {
        heading: 'How to Watermark Identification for Tenancy Applications',
        body: [
          'Whenever you provide digital copies of your identification to a real estate agent:',
          '1. Open the IDFraud.au Watermark Tool.',
          '2. Enter the recipient name: e.g. "Provided to Ray White Surry Hills".',
          '3. Enter the purpose: "Tenancy application for 10/24 Smith St only".',
          '4. Select Diagonal Repeating Grid and set opacity to 45%.',
          '5. Download the watermarked image. This proves to the agent that you hold valid identification, but prevents cybercriminals from repurposing the scan for bank loan applications.'
        ]
      }
    ],
    actionSteps: [
      'Use the IDFraud.au Watermark Tool on all licence and passport copies before submitting rental applications.',
      'Black out your Tax File Number on any payslips or payment summaries.',
      'Redact non-essential transaction descriptions on bank statements.',
      'Ask real estate agents about their data destruction policy for unsuccessful applications.'
    ],
    relatedSlugs: ['australian-driver-licence-protection-guide', 'medicare-card-compromise-replacement-guide', 'birth-certificate-theft-foundation-identity']
  },
  {
    id: 'guide-15-superannuation-fraud',
    slug: 'superannuation-fraud-identity-theft',
    title: 'Protecting Your Superannuation from Identity Thieves: How Criminals Attempt Fraudulent Rollovers',
    category: 'Identity Theft',
    date: '20 July 2026',
    author: {
      name: 'Lachlan Hayes',
      role: 'Identity Security Lead, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 8,
    imageUrl: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Superannuation accounts represent substantial balances targeted by identity thieves through fake Self-Managed Super Funds (SMSFs).',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1450133064473-71024230f91b?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Enabling app-based MFA on your industry superannuation portal stops unauthorized rollover requests.',
    summary: 'Your superannuation nest egg is one of your largest financial assets. Discover how identity criminals attempt fraudulent rollovers into fake SMSFs and how to lock your super accounts.',
    keyTakeaways: [
      'Identity thieves use stolen TFNs, driver licences, and myGov access to create fraudulent Self-Managed Super Funds (SMSFs) in your name.',
      'They request an electronic rollover of your industry or retail superannuation balance into the fraudulent SMSF bank account.',
      'The ATO’s SuperTick and SMSF verification systems flag suspicious rollovers, but proactive account hardening is critical.',
      'Enable Multi-Factor Authentication directly with your super fund (e.g. AustralianSuper, ART, Hostplus, REST) and monitor myGov notifications.'
    ],
    content: [
      'For most working Australians, superannuation is their largest financial asset outside of the family home. With average adult super balances ranging between $100,000 and $500,000 AUD, super funds are high-priority targets for organized financial fraud syndicates.',
      'Criminals do not attempt to withdraw superannuation directly to an everyday checking account; instead, they exploit the Superannuation Rollover framework.',
      'In this guide, we dissect how rollover fraud occurs and the essential measures to protect your retirement savings.'
    ],
    sections: [
      {
        heading: 'How Fraudulent SMSF Rollover Attacks Work',
        body: [
          'The attack involves three steps:',
          '1. Establishing a Fraudulent SMSF: Using stolen identity credentials (TFN, licence, passport), the scammer registers a new Self-Managed Super Fund with the ATO and opens a corresponding commercial bank account.',
          '2. Initiating the Transfer: The scammer submits a rollover request to your legitimate superannuation fund (e.g. AustralianSuper or QSuper) to transfer your entire balance into the newly created SMSF.',
          '3. Exfiltration: Once the funds arrive, the criminal transfers the money overseas or converts it into cryptocurrency before the victim receives quarterly account statements.'
        ]
      }
    ],
    actionSteps: [
      'Enable app-based Multi-Factor Authentication on your super fund portal.',
      'Ensure your contact details (email, mobile) are updated with your super fund.',
      'Inspect ATO superannuation balances on myGov quarterly.',
      'Never disclose superannuation details or member numbers to cold callers.'
    ],
    relatedSlugs: ['tax-file-number-tfn-theft-prevention', 'detecting-mygov-ato-phishing', 'how-to-place-a-credit-ban-australia']
  },
  {
    id: 'guide-16-birth-certificate-theft',
    slug: 'birth-certificate-theft-foundation-identity',
    title: 'Australian Birth Certificate Theft & Identity Replacement: Securing Foundational Records',
    category: 'Australian ID Protection',
    date: '18 July 2026',
    author: {
      name: 'Sarah Chen',
      role: 'Consumer Rights Specialist, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'An Australian birth certificate is a foundational root document required for passports and legal name changes.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1450133064473-71024230f91b?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'State Registries of Births, Deaths and Marriages issue modern replacement certificates with anti-fraud security foils.',
    summary: 'A birth certificate is a foundational identity document. Discover how compromised birth registration numbers can be misused and how state BDM registries handle replacement and security alerts.',
    keyTakeaways: [
      'A birth certificate is a Tier-0 "Foundational Document" used to establish primary identity in Australia.',
      'Compromised birth registration details can be used to apply for fraudulent Australian passports or obtain genuine driver licences under your name.',
      'Contact your State Registry of Births, Deaths & Marriages (BDM) immediately if your certificate is stolen.',
      'Store original physical birth certificates in a locked, fireproof home safe—never carry them in daily wallets or vehicles.'
    ],
    content: [
      'While driver licences and Medicare cards are secondary credentials, your Australian Birth Certificate is a foundational root document. It proves your citizenship, parentage, legal birth name, and date of birth.',
      'If a criminal acquires your original birth certificate or high-resolution scan, they can attempt to build an entirely new identity profile from the ground up.',
      'In this article, we outline the exact safeguards implemented by Australian state BDM registries and the recovery protocol for compromised certificates.'
    ],
    sections: [
      {
        heading: 'How to Report a Compromised Birth Certificate to State BDM',
        body: [
          'If your birth certificate is stolen or lost:',
          '1. File an official police report with state police to obtain a Police Event Number.',
          '2. Contact the Registry of Births, Deaths and Marriages in the state or territory where you were born (e.g. NSW BDM, Births Deaths and Marriages Victoria, QLD Registry).',
          '3. Request a replacement certificate with modern security features. The registry will update your file with the new certificate registration number and flag the previous certificate.'
        ]
      }
    ],
    actionSteps: [
      'Report stolen physical birth certificates to police and your state BDM registry.',
      'Order a replacement certificate with updated security features.',
      'Place a credit ban with Equifax, Experian, and illion.',
      'Never carry original birth certificates in everyday bags or glove compartments.'
    ],
    relatedSlugs: ['australian-passport-lost-stolen-fraud-response', 'australian-driver-licence-protection-guide', 'rental-application-id-theft-watermarking']
  },
  {
    id: 'guide-17-remote-access-scams',
    slug: 'remote-access-scams-teamviewer-anydesk',
    title: 'Remote Access Scams in Australia: Why Legitimate Tech Support Never Asks to Control Your Computer',
    category: 'Scams',
    date: '16 July 2026',
    author: {
      name: 'Marcus Vance',
      role: 'Fraud Intelligence Analyst, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Never install remote desktop software (AnyDesk, TeamViewer) at the request of unsolicited callers.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Disconnect your computer from Wi-Fi immediately if an unverified technician gains screen control.',
    summary: 'Remote access scams trick victims into installing software (AnyDesk, TeamViewer) under the guise of Telstra, NBN, or Microsoft support. Learn how scammers black out screens and drain accounts.',
    keyTakeaways: [
      'Legitimate Australian organizations (Telstra, NBN Co, Microsoft, Apple, Australian banks) will NEVER call unannounced asking you to install remote access software.',
      'Scammers use software like AnyDesk or TeamViewer to gain total control over your computer, browser sessions, and banking tabs.',
      'They black out your computer monitor while transferring funds in the background, claiming they are "cleaning your network router".',
      'If you accidentally granted access, disconnect your Wi-Fi router immediately and call your bank from another phone.'
    ],
    content: [
      'Remote access scams remain one of the most financially devastating fraud types targeting Australian households, especially retirees and home-based professionals.',
      'The scam begins with a phone call claiming to be from "NBN Technical Support", "Telstra Security", or "Microsoft Windows Defender", warning that your home internet is compromised or broadcasting illegal activity.',
      'In this guide, we break down the deception tactics of remote access operators and the emergency decontamination steps required.'
    ],
    sections: [
      {
        heading: 'How Scammers Manipulate Your Screen and Banking',
        body: [
          'Once you install the remote access tool and provide the 9-digit session code, the scammer takes complete control:',
          '1. Fake Event Viewer Errors: They open the Windows Event Viewer command terminal and show routine system logs, claiming they are "dangerous Russian or Chinese malware infections".',
          '2. Directing You to Log In: They ask you to log into your online banking to "receive compensation" or "verify security".',
          '3. Screen Blanking & Transfer: They use software blackout features to turn your screen black while transferring money via Osko, claiming: "Please do not touch your keyboard while the security update completes."'
        ]
      }
    ],
    actionSteps: [
      'Never allow cold callers to access your computer or download remote software.',
      'If you granted access, turn off your computer and disconnect the Wi-Fi router immediately.',
      'Call your bank fraud hotline from a separate mobile phone to freeze accounts.',
      'Take your computer to a certified IT professional to clean installed remote backdoors.'
    ],
    relatedSlugs: ['bank-impersonation-fraud-australian-banking', 'protecting-elderly-parents-from-digital-scams', 'password-security-passkeys-mfa']
  },
  {
    id: 'guide-18-job-scams',
    slug: 'job-offer-employment-recruitment-scams',
    title: 'Fake Employment & Telegram Recruitment Scams Targeting Australian Jobseekers',
    category: 'Scams',
    date: '14 July 2026',
    author: {
      name: 'Sarah Chen',
      role: 'Consumer Rights Specialist, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Unsolicited WhatsApp and Telegram job offers promising $300-$800/day for "app optimization" are task-based scams.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Verify Australian employment offers directly through official employer career pages and ABN lookups.',
    summary: 'Task scams and fake recruitment messages impersonating Michael Page, SEEK, or Hays promise high daily earnings for rating products. Learn how job scams harvest ID and steal deposits.',
    keyTakeaways: [
      'Unsolicited WhatsApp or SMS job offers promising $300–$800 AUD per day for "simple online tasks" are task-based pyramid scams.',
      'Scammers impersonate reputable recruitment agencies (Hays, Michael Page, SEEK, Adecco) using stolen recruiter photos.',
      'The scam requires victims to deposit cryptocurrency or bank transfers to "recharge tasks" before earnings can be withdrawn.',
      'Legitimate Australian employers will NEVER ask candidates to pay training fees, buy crypto, or transfer funds to start work.'
    ],
    content: [
      'Employment scams targeting Australian jobseekers have exploded across WhatsApp, Telegram, and SMS. Exploiting cost-of-living pressures and flexible work-from-home demand, these syndicates promise lucrative daily wages for rating apps, boosting hotel reviews, or optimizing e-commerce listings.',
      'In reality, the victim is lured into an engineered task-deposit trap where they are tricked into depositing thousands of dollars of their own money.',
      'In this article, we expose how the task scam funnel works and how to verify genuine Australian job opportunities.'
    ],
    sections: [
      {
        heading: 'The Task Scam Funnel: How Victims Lose Thousands',
        body: [
          'Phase 1 — The Hook: You receive a WhatsApp message from a "recruiter" offering part-time work requiring only 30 minutes a day.',
          'Phase 2 — The Small Reward: You are given 5 simple tasks on a fake portal and receive a real $50 AUD payout via PayID or crypto to build trust.',
          'Phase 3 — The Recharge Trap: Higher-paying "VIP task bundles" appear with negative balances. You must deposit $500 of your own money to unlock the bundle.',
          'Phase 4 — The Lockout: The required deposit escalates to $2,000, then $8,000. When you attempt to withdraw your funds, the platform demands a 30% "tax release fee", leaving you empty-handed.'
        ]
      }
    ],
    actionSteps: [
      'Never pay money or deposit cryptocurrency to secure a job or complete work tasks.',
      'Verify Australian Business Numbers (ABN) on `abr.business.gov.au`.',
      'Contact recruitment agencies through their official Australian telephone numbers.',
      'Report fake recruitment profiles on WhatsApp, Telegram, and Scamwatch.'
    ],
    relatedSlugs: ['preventing-payid-marketplace-scams', 'tax-file-number-tfn-theft-prevention', 'cryptocurrency-drainer-recovery-scams']
  },
  {
    id: 'guide-19-online-shopping-scams',
    slug: 'online-shopping-scams-fake-storefronts-australia',
    title: 'Identifying Counterfeit Online Retailers & Fake Australian E-Commerce Storefronts',
    category: 'Online Shopping',
    date: '12 July 2026',
    author: {
      name: 'Marcus Vance',
      role: 'Fraud Intelligence Analyst, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 7,
    imageUrl: 'https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Inspect Australian ABNs, physical addresses, and domain registration age before buying from unknown online stores.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1556742049-0a67e5572293?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Use payment methods with chargeback protection (Credit Card, PayPal) rather than direct bank transfers.',
    summary: 'Fake retail storefronts clone popular Australian brands (R.M. Williams, Bunnings, Kathmandu, JB Hi-Fi) with 80% discount clearance sales. Learn how to verify authentic sellers.',
    keyTakeaways: [
      'Counterfeit storefronts advertise on Facebook, Instagram, and TikTok offering 70%–90% off premium Australian brands.',
      'Scammers copy official logos, product photography, and copyright notices onto disposable domains (e.g. `rmwilliams-outlet-au.com`).',
      'Fake stores either deliver counterfeit goods or steal payment card details without shipping any products.',
      'Always test unknown URLs in the IDFraud.au Website Safety Checker before purchasing.'
    ],
    content: [
      'Social media feeds are saturated with sponsored advertisements promoting incredible clearance discounts on iconic Australian brands: $600 leather boots for $79, $400 power tools for $49, and designer apparel at 85% off.',
      'These websites look indistinguishable from the official online store, complete with shopping carts, customer reviews, and security badges.',
      'In this guide, we share the checklist to spot counterfeit Australian e-commerce portals in under 30 seconds.'
    ],
    sections: [
      {
        heading: 'The 4 Red Flags of a Fake Australian Storefront',
        body: [
          '1. Unrealistic Discounts Across All Stock: If every product on the site is discounted by 70% to 90%, it is a counterfeit clone.',
          '2. Missing or Fake ABN: Australian businesses are legally required to display their Australian Business Number (ABN). Search the ABN on `abr.business.gov.au` to verify business name and registration date.',
          '3. Payment Exclusively via Direct Bank Transfer or Crypto: Legitimate retailers offer secure merchant gateways (Apple Pay, PayPal, Visa/Mastercard with 3D Secure).',
          '4. Domain Registered in the Last 30 Days: Scammers create new domains weekly as older sites get blacklisted.'
        ]
      }
    ],
    actionSteps: [
      'Inspect domain age and security telemetry in the IDFraud.au Website Checker.',
      'Verify Australian Business Numbers on the ABN Lookup registry.',
      'Pay exclusively with credit cards or PayPal offering buyer protection chargebacks.',
      'If you entered card details on a fake site, contact your bank immediately to cancel the card.'
    ],
    relatedSlugs: ['australia-post-parcel-delivery-scams', 'preventing-payid-marketplace-scams', 'bank-impersonation-fraud-australian-banking']
  },
  {
    id: 'guide-20-company-director-id-theft',
    slug: 'company-director-small-business-identity-theft',
    title: 'Company Director ID Fraud: Protecting ASIC Records & Australian Small Business Registrations',
    category: 'Identity Theft',
    date: '10 July 2026',
    author: {
      name: 'Lachlan Hayes',
      role: 'Identity Security Lead, IDFraud.au',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=120&auto=format&fit=crop&q=80'
    },
    readingTimeMinutes: 8,
    imageUrl: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&auto=format&fit=crop&q=80',
    imageCaption: 'Company Director ID numbers issued by ABRS protect corporate registry records from fraudulent takeovers.',
    secondaryImageUrl: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&auto=format&fit=crop&q=80',
    secondaryImageCaption: 'Auditing ASIC company extract details to ensure unauthorized directors have not been appointed.',
    summary: 'Small business owners and company directors face severe threats from corporate identity theft. Learn how criminals alter ASIC company records, obtain commercial credit, and how Director IDs safeguard businesses.',
    keyTakeaways: [
      'Criminals use stolen identity details to lodge fraudulent ASIC forms (e.g. Form 484) changing company director names and registered addresses.',
      'Once in control of the corporate registry profile, they apply for large business loans, commercial equipment leases, and trade credit in your company’s name.',
      'All Australian company directors must hold a Director Identification Number (Director ID) issued by the Australian Business Registry Services (ABRS).',
      'Set up proactive ASIC registry alerts and review your company extract annually to detect unauthorized filings.'
    ],
    content: [
      'In Australia, small business owners and corporate directors hold dual risk profiles: personal identity vulnerability and business entity exposure.',
      'Corporate identity theft occurs when criminals manipulate public registry records held by the Australian Securities and Investments Commission (ASIC) and the Australian Business Register (ABR).',
      'By falsely appointing themselves as directors or redirecting the company’s registered office address, fraudsters acquire corporate credit lines, order thousands of dollars of commercial goods on 30-day trade terms, and leave the legitimate business owner with the debt.',
      'In this article, we explain how corporate identity theft happens and the essential safeguards for Australian business leaders.'
    ],
    sections: [
      {
        heading: 'How Criminals Hijack Australian Company Registrations',
        body: [
          'The corporate hijacking process involves three vulnerabilities:',
          '1. Paper-Based ASIC Filings: Fraudsters submit fraudulent ASIC Form 484 documents using forged signatures and stolen director licences, altering the registered office address and appointing a criminal nominee.',
          '2. Fraudulent Commercial Credit: Armed with a clean 10-year-old Australian company name and ACN, the criminals apply for commercial trade credit with building suppliers, electronics wholesalers, and vehicle leasing firms.',
          '3. Unpaid Debt Collections: Months later, the real business owner receives aggressive debt collection notices and court writs for goods they never ordered.'
        ]
      },
      {
        heading: 'How Director ID and ABRS Protect Corporate Records',
        body: [
          'To combat corporate identity theft, the Commonwealth established the Australian Business Registry Services (ABRS) and mandatory Director Identification Numbers (Director IDs).',
          'A Director ID is a unique 15-digit identifier that a director applies for once and keeps forever. It requires robust identity verification through myGovID with passport and driver licence validation, preventing criminals from establishing fictitious directorships under stolen names.'
        ]
      }
    ],
    actionSteps: [
      'Ensure your Director Identification Number is registered with ABRS (`abrs.gov.au`).',
      'Register for ASIC corporate portal electronic notifications to receive instant email alerts for any registry changes.',
      'Perform an annual ASIC company extract search to verify current registered details.',
      'Place a credit ban on your personal and commercial files if business identity data is exposed.'
    ],
    relatedSlugs: ['tax-file-number-tfn-theft-prevention', 'how-to-place-a-credit-ban-australia', 'australian-driver-licence-protection-guide']
  }
];
